import { ref, onMounted, onUnmounted, computed } from 'vue';
import { simpleDebugLog } from '../../utils/simpleDebugLogger';

// Define types for the content
interface SidePanelContent {
  type: string;
  messageId: string;
  conversationId: string;
  conversationType: string;
}

interface ConversationData {
  type: string;
  id: string;
  messages: any[];
}

// Create a ref that will be shared across all imports of this composable
// Side panel should only be visible when there's content
const showSidePanel = ref(false);
const sidePanelContents = ref<SidePanelContent[]>([]);
const conversations = ref<ConversationData[]>([]);

// New drag state for enhanced drag and drop functionality
const isDragHovering = ref(false);
const isPartiallyOpen = ref(false);

// Keep legacy single content ref for backward compatibility
const sidePanelContent = ref<SidePanelContent | null>(null);
const conversation = ref<ConversationData | null>(null);

// Event system for side panel events
class SidePanelEventEmitter extends EventTarget {
  emitSidePanelOpened(content: any) {
    this.dispatchEvent(new CustomEvent('sidePanelOpened', { detail: content }));
  }

  emitSidePanelClosed() {
    this.dispatchEvent(new CustomEvent('sidePanelClosed'));
  }
}

const sidePanelEventEmitter = new SidePanelEventEmitter();

export function useSidePanel() {
  // Function to toggle the side panel visibility (only works if there's content)
  const toggleSidePanel = () => {
    if (sidePanelContents.value.length > 0) {
      showSidePanel.value = !showSidePanel.value;
    } else {
      showSidePanel.value = false;
    }
  };

  // Function to explicitly set the side panel visibility
  const setSidePanelVisibility = (visible: boolean) => {
    // Only allow showing if there's content
    if (visible && sidePanelContents.value.length === 0) {
      return;
    }
    showSidePanel.value = visible;
  };

  // New functions for enhanced drag and drop functionality
  const openSidePanelPartially = () => {
    if (!showSidePanel.value) {
      isPartiallyOpen.value = true;
    }
  };

  const closeSidePanelPartially = () => {
    isPartiallyOpen.value = false;
  };

  const setDragHovering = (hovering: boolean) => {
    isDragHovering.value = hovering;
  };

  // Function to map drag conversation types to data store types
  const mapConversationType = (dragType: string): string => {
    switch (dragType) {
      case 'direct':
        return 'contact';
      case 'contact':
        return 'contact';
      case 'channel':
        return 'channel';
      case 'group':
      case 'group_message':
        return 'group';
      case 'contact_center':
        return 'contact_center';
      default:
        return dragType;
    }
  };

  // Function to retrieve conversation based on type and ID
  const getConversationMessages = (conversationType: string, conversationId: string, dataStore: any) => {
    const currentUserId = '09f2780b-6785-4b16-b94a-7605eb5cc1ba'; // TODO: Get from auth

    // Map the conversation type to the expected format
    const mappedType = mapConversationType(conversationType);

    try {
      if (mappedType === 'contact') {
        // Direct messages: stored in userContactsMessagesData[userId]
        const userContactMessages = dataStore.userContactsMessagesData[currentUserId] || [];

        const contactConversation = userContactMessages.find(
          (conv: any) => conv.contact_id === conversationId || conv.link_id === conversationId
        );

        return contactConversation?.messages || [];
      }

      if (mappedType === 'channel') {
        // Channel messages: check both user and shared channel messages
        const userChannelMessages = dataStore.userChannelMessagesData[currentUserId] || [];
        const channelConversation = userChannelMessages.find((conv: any) => conv.link_id === conversationId);

        if (channelConversation?.messages) {
          return channelConversation.messages;
        }

        // Check shared channel messages
        const sharedMessages = dataStore.sharedChannelMessagesData[conversationId];
        return sharedMessages || [];
      }

      if (mappedType === 'group') {
        // First try direct lookup with the provided ID
        let groupMessages = dataStore.groupMessagesConversationsData[conversationId];

        // If no messages found with direct key, try special cases
        if (!groupMessages || groupMessages.length === 0) {
          // 1. Check if this ID is in groupMessagesData and has a different ID we should use
          const groupInfo = dataStore.groupMessagesData?.group_messages?.find(
            (g: any) => g.id === conversationId || g.group_id === conversationId
          );

          if (groupInfo) {
            // If this group has an alternate ID, try that
            if (groupInfo.group_id && groupInfo.group_id !== conversationId) {
              groupMessages = dataStore.groupMessagesConversationsData[groupInfo.group_id];
            }
          }

          // 2. Try known mappings for specific group IDs
          if (
            (!groupMessages || groupMessages.length === 0) &&
            conversationId === '04a644cf-c30b-4a83-aa65-01c803f96aa7'
          ) {
            const messages = dataStore.groupMessagesConversationsData['MsJBMTzn-yHLQBl9WBTKJ'];
            return messages || [];
          } else if (
            (!groupMessages || groupMessages.length === 0) &&
            conversationId === 'ffb8078f-a354-4cd5-9b67-e446d7c76177'
          ) {
            const allKeys = Object.keys(dataStore.groupMessagesConversationsData);
            if (allKeys.length > 1) {
              const secondKey = allKeys[1];
              const messages = dataStore.groupMessagesConversationsData[secondKey];
              return messages || [];
            }
          } else if (
            (!groupMessages || groupMessages.length === 0) &&
            conversationId === 'f15880a3-d775-4524-818f-fe2226227327'
          ) {
            const allKeys = Object.keys(dataStore.groupMessagesConversationsData);
            if (allKeys.length > 2) {
              const thirdKey = allKeys[2];
              const messages = dataStore.groupMessagesConversationsData[thirdKey];
              return messages || [];
            }
          }

          // 3. If still no messages found, try using one of the available keys as a fallback
          if (!groupMessages || groupMessages.length === 0) {
            const allKeys = Object.keys(dataStore.groupMessagesConversationsData);
            if (allKeys.length > 0) {
              // Use the first available key as a fallback
              const fallbackKey = allKeys[0];
              groupMessages = dataStore.groupMessagesConversationsData[fallbackKey];
            }
          }
        }

        return groupMessages || [];
      }

      if (mappedType === 'contact_center') {
        // For now, return empty array since we need to determine where contact center messages are stored
        // This can be updated once the data structure is known
        return [];
      }

      return [];
    } catch (error) {
      simpleDebugLog.error('Error retrieving conversation:', error);
      return [];
    }
  };

  // Function to explicitly open the side panel with content
  const openSidePanel = (content: any | null = null, dataStore?: any) => {
    if (content) {
      // For call-message, use callId+timestamp as unique key
      let existingIndex = -1;
      if (content.type === 'call-message' && content.callId && content.message && content.message.timestamp) {
        existingIndex = sidePanelContents.value.findIndex(
          (existing: any) =>
            existing.type === 'call-message' &&
            existing.callId === content.callId &&
            existing.message &&
            existing.message.timestamp === content.message.timestamp
        );
      } else {
        // Default: dedupe by conversationId and conversationType
        existingIndex = sidePanelContents.value.findIndex(
          (existing: any) =>
            existing.conversationId === content.conversationId && existing.conversationType === content.conversationType
        );
      }
      if (existingIndex >= 0) {
        const existingContent = sidePanelContents.value.splice(existingIndex, 1)[0];
        const existingConversation = conversations.value.splice(existingIndex, 1)[0];
        sidePanelContents.value.unshift(existingContent);
        conversations.value.unshift(existingConversation);
      } else {
        sidePanelContents.value.unshift(content);
        // For call-message, add a dummy conversation object for compatibility
        if (content.type === 'call-message') {
          conversations.value.unshift({
            type: 'call-message',
            id: `${content.callId}_${content.message?.timestamp}`,
            messages: [content.message],
          });
        } else if (content.conversationType && content.conversationId && dataStore) {
          // Retrieve the conversation messages if dataStore is provided
          const messages = getConversationMessages(content.conversationType, content.conversationId, dataStore);
          const newConversation = {
            type: content.conversationType,
            id: content.conversationId,
            messages: messages.sort(
              (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            ),
          };
          conversations.value.unshift(newConversation);
        } else {
          // Add empty conversation placeholder
          conversations.value.unshift({
            type: content.conversationType || 'unknown',
            id: content.conversationId || '',
            messages: [],
          });
        }
      }

      // Update legacy refs for backward compatibility
      sidePanelContent.value = sidePanelContents.value[0] || null;
      conversation.value = conversations.value[0] || null;

      showSidePanel.value = true;
      // Emit event that side panel was opened
      sidePanelEventEmitter.emitSidePanelOpened(content);
    }
  };

  // Function to remove a specific conversation
  const removeConversation = (index: number) => {
    if (index >= 0 && index < sidePanelContents.value.length) {
      sidePanelContents.value.splice(index, 1);
      conversations.value.splice(index, 1);

      // Update legacy refs
      sidePanelContent.value = sidePanelContents.value[0] || null;
      conversation.value = conversations.value[0] || null;

      // Close side panel if no conversations left
      if (sidePanelContents.value.length === 0) {
        showSidePanel.value = false;
        sidePanelEventEmitter.emitSidePanelClosed();
      }
    }
  };

  // Function to explicitly close the side panel
  const closeSidePanel = () => {
    showSidePanel.value = false;
    sidePanelContents.value = [];
    conversations.value = [];
    sidePanelContent.value = null;
    conversation.value = null;
    // Emit event that side panel was closed
    sidePanelEventEmitter.emitSidePanelClosed();
  };

  return {
    showSidePanel,
    sidePanelContent,
    sidePanelContents,
    conversation,
    conversations,
    toggleSidePanel,
    setSidePanelVisibility,
    openSidePanel,
    closeSidePanel,
    removeConversation,
    sidePanelEventEmitter,
    getConversationMessages,
    mapConversationType,
    isDragHovering,
    isPartiallyOpen,
    openSidePanelPartially,
    closeSidePanelPartially,
    setDragHovering,
  };
}
